import React, { useRef, useState } from "react";
import { Check, Camera, X, ImageIcon, AlertCircle, ShieldAlert } from "lucide-react";
import { API } from "@/lib/api";
import { toast } from "sonner";

function CorrectiveNote({ existing, onSave }) {
  const [note, setNote] = useState(existing || "");
  const [saving, setSaving] = useState(false);
  const dirty = note.trim() !== (existing || "").trim();
  const submit = async () => {
    if (!note.trim()) { toast.error("Enter a corrective action note"); return; }
    setSaving(true);
    try { await onSave(note.trim()); } finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl border border-[#EF4444]/50 bg-[#EF4444]/[0.06] p-3">
      <p className="flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wide text-[#EF4444] mb-2">
        <ShieldAlert className="h-3.5 w-3.5" /> Corrective action required
      </p>
      <textarea
        data-testid="corrective-note-input"
        value={note}
        onChange={(e) => setNote(e.target.value)}
        rows={2}
        placeholder="What was done? (e.g. moved product to walk-in, called refrigeration tech)"
        className="w-full rounded-lg bg-[#0E0E0E] border border-[#2E2E2E] px-3 py-2 text-sm outline-none focus:border-[#EF4444] transition-colors resize-none"
      />
      <div className="flex items-center justify-between mt-2">
        <span className="text-[11px] text-[#6B6B6B]">{existing && !dirty ? "Saved — required before finalizing" : "Required before this checklist can be finalized"}</span>
        <button
          data-testid="corrective-save-button"
          onClick={submit}
          disabled={saving || !dirty}
          className="rounded-lg bg-[#EF4444] text-white text-xs font-semibold px-3 py-1.5 hover:bg-[#B91C1C] disabled:opacity-50 transition-colors"
        >
          {saving ? "Saving…" : existing && !dirty ? "Saved" : "Save note"}
        </button>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); }
  catch { return ""; }
}

function tempOutOfRange(expected, value) {
  if (!expected || value == null || value === "") return false;
  const nums = String(expected).match(/-?\d+(\.\d+)?/g);
  const m = String(value).match(/-?\d+(\.\d+)?/);
  if (!nums || !m) return false;
  const v = parseFloat(m[0]);
  const low = String(expected).toLowerCase();
  if (low.includes("below") || low.includes("under")) return v > parseFloat(nums[0]);
  if (low.includes("above") || low.includes("over") || low.includes("least")) return v < parseFloat(nums[0]);
  if (nums.length >= 2) return v < parseFloat(nums[0]) || v > parseFloat(nums[1]);
  return false;
}

export default function ChecklistItem({ item, text, meta, photo, disabled, allowPhoto, onToggle, onPhoto, onRemovePhoto, onCorrective }) {
  const it = item || { text };
  const checked = !!meta?.checked;
  const isInput = !!it.input;
  const requirePhoto = !!it.require_photo;
  const fileRef = useRef(null);
  const [val, setVal] = useState(meta?.value || "");
  const token = localStorage.getItem("pb_token");
  const photoUrl = photo ? `${API}/files/${photo.path}?auth=${token}` : null;

  const handleToggle = () => {
    if (!checked && isInput && !val.trim()) {
      toast.error("Enter the counted amount first");
      return;
    }
    onToggle(!checked, isInput ? val.trim() : undefined);
  };

  const photoMissing = requirePhoto && !photo;
  const oorLive = isInput && !checked && !!val && tempOutOfRange(it.expected, val);
  const oorChecked = checked && !!meta?.out_of_range;

  return (
    <div className={`rounded-xl border transition-all ${oorChecked ? "bg-[#EF4444]/10 border-[#EF4444]/50" : checked ? "bg-[#16A34A]/10 border-[#16A34A]/40" : photoMissing ? "bg-[#171717] border-[#EF4444]/40" : "bg-[#171717] border-[#2E2E2E]"}`}>
      <div className="flex items-start gap-3.5 p-4">
        <button
          data-testid="checklist-item-checkbox"
          disabled={disabled}
          onClick={handleToggle}
          className="flex items-start gap-3.5 flex-1 min-w-0 text-left disabled:cursor-not-allowed active:scale-[0.99] transition-transform"
        >
          <span className={`mt-0.5 h-6 w-6 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${checked ? "bg-[#16A34A] border-[#16A34A]" : "border-[#3a3a3a]"}`}>
            {checked && <Check className="h-4 w-4 text-white check-pop" strokeWidth={3} />}
          </span>
          <span className="flex-1 min-w-0">
            <span className={`block text-[15px] leading-snug ${checked ? "text-[#A3A3A3] line-through" : "text-[#F5F5F5]"}`}>
              {it.text}
            </span>
            <span className="flex flex-wrap items-center gap-x-2 gap-y-1 mt-1">
              {requirePhoto && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-mono ${photoMissing ? "text-[#EF4444]" : "text-[#16A34A]"}`}>
                  <Camera className="h-3 w-3" /> {photoMissing ? "Photo required" : "Photo added"}
                </span>
              )}
              {checked && meta && (
                <span data-testid="checklist-item-timestamp" className={`inline-flex items-center gap-1.5 font-mono text-[11px] ${oorChecked ? "text-[#EF4444]" : "text-[#16A34A]"}`}>
                  {oorChecked ? <AlertCircle className="h-3 w-3" /> : <Check className="h-3 w-3" />}
                  {meta.value ? `${meta.value}${oorChecked ? " ⚠ out of range" : ""} · ` : ""}{fmtTime(meta.checked_at)} · {meta.checked_by}
                </span>
              )}
            </span>
          </span>
        </button>

        {allowPhoto && !disabled && (
          <>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" data-testid="photo-input"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) onPhoto(f); e.target.value = ""; }} />
            <button
              data-testid="photo-capture-button"
              onClick={() => fileRef.current?.click()}
              className={`shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center transition-colors ${photo ? "border-[#E11D2A] bg-[#E11D2A]/15 text-[#E11D2A]" : photoMissing ? "border-[#EF4444]/60 text-[#EF4444]" : "border-[#2E2E2E] bg-[#0E0E0E] text-[#A3A3A3] hover:border-[#E11D2A]/50"}`}
              title={photo ? "Replace photo" : "Add photo proof"}
            >
              {photo ? <ImageIcon className="h-4 w-4" /> : <Camera className="h-4 w-4" />}
            </button>
          </>
        )}
      </div>

      {isInput && !checked && !disabled && (
        <div className="px-4 pb-4 -mt-1">
          <div className="flex items-center gap-2">
            <AlertCircle className={`h-4 w-4 shrink-0 ${oorLive ? "text-[#EF4444]" : "text-[#E11D2A]"}`} />
            <input
              data-testid="count-input"
              value={val}
              onChange={(e) => setVal(e.target.value)}
              placeholder={it.expected ? `Enter reading (target ${it.expected})` : "Enter amount"}
              className={`flex-1 rounded-lg bg-[#0E0E0E] border px-3 py-2 text-sm outline-none transition-colors ${oorLive ? "border-[#EF4444] text-[#EF4444] focus:border-[#EF4444]" : "border-[#2E2E2E] focus:border-[#E11D2A]"}`}
            />
          </div>
          {oorLive && <p className="mt-1.5 ml-6 text-[11px] text-[#EF4444]">⚠ Out of range (target {it.expected}) — a manager will be alerted.</p>}
        </div>
      )}

      {oorChecked && !disabled && onCorrective && (
        <div className="px-4 pb-4 -mt-1">
          <CorrectiveNote existing={meta.corrective} onSave={onCorrective} />
        </div>
      )}
      {oorChecked && disabled && meta.corrective && (
        <div className="px-4 pb-4 -mt-1 flex items-start gap-1.5 text-[11px] text-[#E5A100]">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0 mt-0.5" /> <span>Corrective: {meta.corrective}</span>
        </div>
      )}

      {photo && (
        <div className="px-4 pb-4 -mt-1">
          <div className="relative inline-block">
            <a href={photoUrl} target="_blank" rel="noopener noreferrer">
              <img data-testid="photo-thumbnail" src={photoUrl} alt="proof" className="h-24 w-24 rounded-lg object-cover border border-[#2E2E2E]" />
            </a>
            <span className="ml-2 align-bottom inline-block font-mono text-[11px] text-[#6B6B6B]">{photo.by} · {fmtTime(photo.at)}</span>
            {!disabled && (
              <button data-testid="photo-remove-button" onClick={() => onRemovePhoto()} className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-[#E11D2A] text-white flex items-center justify-center shadow-lg">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
